import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { captureException } from '@sentry/react';
import { CustomError } from 'typescript/utilities/custom_error';
import { useLocaleInfo } from 'typescript/context/locale_info';
import { CountryCode, LanguageCode } from 'typescript/constants/localization';
import {
  completeOrder as clientCompleteOrder,
  getOrderSummary as clientGetOrderSummary,
  updateOrderAddressInformation as clientUpdateOrderAddressInformation,
  requestRecaptchaFallback as clientRequestRecaptchaFallback,
} from 'typescript/clients/solidshop/order_actions_client';
import { QUERY_KEYS, MUTATION_KEYS } from 'typescript/constants/react_query';

export const useGetOrderSummary = ({
  orderNumber,
  countryCode,
  languageCode,
}: {
  orderNumber: string;
  countryCode?: CountryCode;
  languageCode?: LanguageCode;
}) => {
  const { countryCode: contextCountryCode, languageCode: contextLanguageCode } =
    useLocaleInfo();

  const { data: orderSummaryData, ...rest } = useQuery({
    queryKey: [QUERY_KEYS.ORDER_SUMMARY, { orderNumber }],
    queryFn: () =>
      clientGetOrderSummary({
        orderNumber,
        countryCode: countryCode ?? contextCountryCode,
        languageCode: languageCode ?? contextLanguageCode,
      }),
    staleTime: 1000 * 60 * 5, // keep the data fresh for 5 minutes so mounting new components doesn't trigger a refetch
  });

  return { orderSummaryData, ...rest };
};

export const useUpdateOrderAddressInformation = ({
  orderNumber,
}: {
  orderNumber: string;
}) => {
  const { countryCode: contextCountryCode, languageCode: contextLanguageCode } =
    useLocaleInfo();
  const queryClient = useQueryClient();
  const queryKey = [
    MUTATION_KEYS.UPDATE_ORDER_ADDRESS_INFORMATION,
    { orderNumber },
  ];

  const {
    mutate: updateOrderAddressInformation,
    mutateAsync: updateOrderAddressInformationAsync,
    ...rest
  } = useMutation({
    mutationKey: queryKey,
    mutationFn: ({
      address,
      countryCode = contextCountryCode,
      languageCode = contextLanguageCode,
    }: {
      address: Record<string, string | boolean>;
      countryCode?: CountryCode;
      languageCode?: LanguageCode;
    }) =>
      clientUpdateOrderAddressInformation({
        address,
        countryCode,
        languageCode,
        orderNumber,
      }),
    onError: (error) =>
      captureException(
        new CustomError(MUTATION_KEYS.UPDATE_ORDER_ADDRESS_INFORMATION, error),
        {
          extra: {
            orderNumber,
          },
        },
      ),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDER_SUMMARY, { orderNumber }],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.PAYMENT_OPTIONS, { orderNumber }],
      });
    },
  });

  const isAnyPending = useIsMutating({ mutationKey: queryKey }) > 0;

  return {
    updateOrderAddressInformation,
    updateOrderAddressInformationAsync,
    isAnyPending,
    ...rest,
  };
};

export const useCompleteOrder = ({ orderNumber }: { orderNumber: string }) => {
  const { countryCode: contextCountryCode, languageCode: contextLanguageCode } =
    useLocaleInfo();
  const queryClient = useQueryClient();

  const { mutate: completeOrder, ...rest } = useMutation({
    mutationKey: [
      MUTATION_KEYS.ORDER_COMPLETE,
      { orderNumber, contextLanguageCode },
    ],
    mutationFn: ({
      recaptchaResponse,
      financingDisclosureAcknowledged,
      useSavedPaymentSource,
      makeDefaultCard,
      paymentPostalCode,
      languageCode = contextLanguageCode,
      countryCode = contextCountryCode,
    }: {
      recaptchaResponse: string;
      financingDisclosureAcknowledged?: boolean;
      useSavedPaymentSource?: boolean;
      makeDefaultCard?: boolean;
      paymentPostalCode?: string;
      countryCode?: CountryCode;
      languageCode?: LanguageCode;
    }) =>
      clientCompleteOrder({
        recaptchaResponse,
        financingDisclosureAcknowledged,
        useSavedPaymentSource,
        makeDefaultCard,
        paymentPostalCode,
        countryCode,
        languageCode,
        orderNumber,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.ORDER_SUMMARY, { orderNumber }],
      });
    },
    onError: (error) =>
      captureException(new CustomError(MUTATION_KEYS.ORDER_COMPLETE, error), {
        extra: {
          orderNumber,
        },
      }),
  });

  return { completeOrder, ...rest };
};

export const useRequestRecaptchaFallback = ({
  orderNumber,
  countryCode,
  languageCode,
}: {
  orderNumber: string;
  countryCode: CountryCode;
  languageCode: LanguageCode;
}) => {
  const { mutateAsync: requestRecaptchaFallback, ...rest } = useMutation({
    mutationKey: [MUTATION_KEYS.RECAPTCHA_FALLBACK, { orderNumber }],
    mutationFn: async () => {
      const response = await clientRequestRecaptchaFallback({
        orderNumber,
        countryCode,
        languageCode,
      });

      return `challenge_${response.challenge_token}`;
    },
    onError: (error) =>
      captureException(
        new CustomError(MUTATION_KEYS.RECAPTCHA_FALLBACK, error),
        {
          extra: { orderNumber },
        },
      ),
  });

  return { requestRecaptchaFallback, ...rest };
};
